import { compact, isEmpty, map, sortBy } from 'lodash/fp';

import {
  Award,
  CommonQuestions,
  ContentTiles,
  ExplainedTerm,
  Offer,
  Placeholder,
  AwardOrCommonQuestionsOrContentTilesOrHowToOrProductListOrRelatedTopicsUrlOrSellingPointGroupOrTopicGroupOrValuePropositionsObject as RefersToContent,
  ValuePropositionsObject,
} from '../../../../types/__codegen__/sanity/graphql';
import { CONTENT_SECTIONS } from '../../content/sections';
import { EXPLAINED_TERMS_SLUG_BLOCKLIST } from '../../../../../../pages/utils/is-in-explained-blocklist';
import { ProductType } from '../../../../types/product';
import { TableOfContentsItem } from '../../../../types/sanityInternal';
import getExtrasProductListingContent from './extras-product-listing-content';
import getHospitalProductListingContent from './get-hospital-product-listing-content';
import mapAwardsContent from './map-awards-content';
import mapContactContent from './map-contact-content';
import mapHeaderContent from './map-header-content';
import mapOfferContent from './map-offer-content';
import mapQuestionsContent from './map-questions-content';
import mapSellingPointsContent from './map-selling-points-content';
import mapTilesContent from './map-tiles-content';
import mapValuePropositionsContent from './map-value-propositions-content';
import uncapFpIterator from '../../../../../utils/uncap-fp-iterator';

const mapWithIndex = uncapFpIterator(map);

const productListingContentLookup = {
  [EXPLAINED_TERMS_SLUG_BLOCKLIST.EXTRAS_COVER_LANDING_PAGE]: getExtrasProductListingContent,
  [EXPLAINED_TERMS_SLUG_BLOCKLIST.HOSPITAL_COVER_LANDING_PAGE]: getHospitalProductListingContent,
};

type GetContentToRenderProps = {
  cmsData: {
    contact: {
      items: TableOfContentsItem[];
      name: string;
    };
    explainedTerm: ExplainedTerm;
  };
  offerContentToRender: {
    overrideOffers: Offer[];
    placeholders: Placeholder[];
    targetSegment: string;
  } | null;
  products?: Record<string, ProductType>;
  slug?: string;
};

type Index = { orderIndex: number };

const getContentToRender = ({ cmsData, offerContentToRender, products, slug }: GetContentToRenderProps) => {
  const explainedTermData = cmsData?.explainedTerm;
  const contactData = cmsData?.contact;
  const sectionsArray = [];
  const removePaddingTop = !isEmpty(offerContentToRender);

  const headerSection = mapHeaderContent({ cmsData: explainedTermData, removePaddingTop });
  const offerSection = mapOfferContent(offerContentToRender);

  sectionsArray.push(offerSection);
  sectionsArray.push(headerSection);

  if (products && slug) {
    const getProductListingContent = productListingContentLookup[slug];
    sectionsArray.push(getProductListingContent && getProductListingContent(products));
  }

  const contentSections = explainedTermData?.refersToContent;
  const sectionsWithIndex: (RefersToContent & Index)[] = mapWithIndex((section: RefersToContent, index: number) => ({
    ...section,
    orderIndex: index + 1,
  }))(contentSections);

  const sellingPointsSection = mapSellingPointsContent({ refersToContent: sectionsWithIndex });
  sectionsArray.push(sellingPointsSection);

  const formattedSections = sectionsWithIndex.map((section) => {
    const contentType = section?.__typename;

    if (contentType === CONTENT_SECTIONS.VALUE_PROPOSITIONS) {
      return mapValuePropositionsContent({ cmsSectionContent: section as ValuePropositionsObject & Index });
    }

    if (contentType === CONTENT_SECTIONS.TILES) {
      return mapTilesContent({ cmsSectionContent: section as ContentTiles & Index });
    }

    if (contentType === CONTENT_SECTIONS.COMMON_QUESTIONS) {
      return mapQuestionsContent({ cmsSectionContent: section as CommonQuestions & Index });
    }

    // TODO - Award needs to be included in the Explained Term sanity model
    if (contentType === CONTENT_SECTIONS.AWARD) {
      return mapAwardsContent({ cmsSectionContent: section as Award & Index });
    }

    return null;
  });

  const contactSection = mapContactContent({ cmsData: contactData });
  sectionsArray.push(contactSection);
  const orderedSections = sortBy('orderIndex', compact([...sectionsArray, ...formattedSections]));

  return {
    ...cmsData,
    sections: orderedSections,
  };
};

export default getContentToRender;
